import { useEffect, useState } from 'react';
import { GivenPermission } from '../../../../types/Permission';
import { Checkbox, CircularProgress, FormControl, InputLabel, TextField } from '@mui/material';
import { assignUserPermission, getUserPermissions, revokeUserPermission } from '../../../../services/user.service';
import { SingleResult } from '../../../../types/SingleResult';
import { AssignUserPermissions } from '../../../../constants/Constants';
import { makeStyles } from '@mui/styles';
import { useNotifyMessage } from '../../../../hooks/useNotifyMessage';

interface UserPermissionsProps {
  userId: string;
  isFromAppUserConfig?: boolean;
}

const useStyles = makeStyles(() => ({
  container_loadingIndicator: {
    float: 'right',
    marginTop: '-65px',
    display: 'flex',
  },
  container_legend: {
    position: 'fixed',
    border: '1px solid rgba(0, 0, 0, 0.12)',
    paddingTop: '10px',
    paddingRight: '10px',
    opacity: 0.3,
    zIndex: 1,
    '&:hover': {
      opacity: 1,
    },
  },
  container_legend_top: {
    top: '55px',
    right: '50px',
  },
  container_legend_bottom: {
    bottom: '25px',
    right: '20px',
  },
  container_permissions: {
    display: 'flex',
    flexDirection: 'column',
  },
  loadingIndicator_main: {
    marginTop: '10px',
  },
  loadingIndicator_permission: {
    marginTop: '4px',
    marginRight: '15px',
    marginLeft: '5.5px',
  },
  inputlabel_clickable: {
    cursor: 'pointer',
    width: 'auto',
  },
  inputLabel_permission: {
    height: '30px',
    cursor: 'pointer',
    width: 'auto',
    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.03)',
    },
  },
  w_100: {
    width: '100%',
  },
  formControl_search: {
    margin: 1,
    marginLeft: 0,
    width: '350px',
  },
}));

const UserPermissions = (props: UserPermissionsProps) => {
  const classes = useStyles();
  const { notifyMessage } = useNotifyMessage();

  const [permissions, setPermissions] = useState<readonly GivenPermission[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [loadingPermissions, setLoadingPermissions] = useState<string[]>([]);
  const [showOnlyAssigned, setShowOnlyAssigned] = useState<boolean>(false);
  const [searchValue, setSearchValue] = useState<string>('');

  const handleGivenPermissionsReceived = (result: SingleResult<GivenPermission[]>, forPermissionCode: string | null) => {
    if (result.status == 200) setPermissions(result.data);
    else if (result.status == 403) notifyMessage(result.data.toString(), 'error');

    if (forPermissionCode) setLoadingPermissions(loadingPermissions.filter((x) => x !== forPermissionCode));
  };

  const onCheckBoxChange = (x: GivenPermission): void => {
    setLoadingPermissions([...loadingPermissions, x.code]);
    setIsLoading(true);
    if (x.isAssigned) {
      revokeUserPermission(props.userId, x.code)
        .then((result) => handleGivenPermissionsReceived(result, x.code))
        .finally(() => setIsLoading(false));
    } else {
      assignUserPermission(props.userId, x.code)
        .then((result) => handleGivenPermissionsReceived(result, x.code))
        .finally(() => setIsLoading(false));
    }
  };

  useEffect(() => {
    setIsLoading(true);
    getUserPermissions(props.userId) //fetch permissions
      .then((result) => handleGivenPermissionsReceived(result, null))
      .finally(() => setIsLoading(false));
  }, [props.userId]);

  return (
    <div>
      {!permissions.length && !isLoading && <span className='txt-muted'>{AssignUserPermissions.noPermissions}</span>}
      {permissions.length > 0 && (
        <>
          <div className={classes.container_loadingIndicator}>
            <CircularProgress className={classes.loadingIndicator_main} size={20} sx={{ visibility: isLoading ? 'visible' : 'hidden' }} />
            <InputLabel className={classes.inputlabel_clickable}>
              <Checkbox checked={showOnlyAssigned} onChange={() => setShowOnlyAssigned(!showOnlyAssigned)} />
              {AssignUserPermissions.onlyShowAssigned}
            </InputLabel>
          </div>
          <div
            className={`${classes.container_legend} ${props.isFromAppUserConfig ? classes.container_legend_top : classes.container_legend_bottom}`}
          >
            <InputLabel className={classes.w_100}>
              <Checkbox checked={true} disabled={false} />
              {AssignUserPermissions.legend_assigned_directly}
            </InputLabel>
            <InputLabel className={classes.w_100}>
              <Checkbox checked={true} disabled={true} />
              {AssignUserPermissions.legend_assigned_indirectly}
            </InputLabel>
          </div>
        </>
      )}
      {permissions.length > 0 && (
        <FormControl variant='outlined' className={classes.formControl_search}>
          <TextField
            size='small'
            sx={{ flex: 1 }}
            placeholder={AssignUserPermissions.search}
            value={searchValue}
            onChange={(newValue) => {
              setSearchValue(newValue.target.value);
            }}
          />
        </FormControl>
      )}
      <div className={classes.container_permissions}>
        {permissions
          .filter(
            (x) =>
              (!showOnlyAssigned || x.isAssigned) && // filter on "show only assigned" checkbox
              (!searchValue || x.code.toLowerCase().includes(searchValue.toLowerCase())) // filter for search text field
          )
          .map((x, idx) => (
            <InputLabel key={idx} className={classes.inputLabel_permission}>
              {!loadingPermissions.includes(x.code) && (
                <Checkbox
                  checked={x.isAssigned}
                  disabled={x.isIndirectlyGranted}
                  readOnly={x.isIndirectlyGranted}
                  onChange={() => !isLoading && onCheckBoxChange(x)}
                />
              )}
              {loadingPermissions.includes(x.code) && <CircularProgress size={20} className={classes.loadingIndicator_permission} />}
              {x.code}
              <span className='txt-muted txt-small'>&nbsp;-&nbsp;{x.description}</span>
            </InputLabel>
          ))}
      </div>
    </div>
  );
};

export default UserPermissions;
